import React from 'react';
import HeroSection from './Hero';
import Chairman from './Images/Chairman.png';
import ViseChairman from './Images/ViseChairman.png';
import {
  Box,
  Typography,
  Container,
  Grid,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import MissionVisionSection from './MissionVisionSection';
import ImageSlider from './ImageSlider';
import OurValues from './OurValues';
import PartnersSection from './PartnersSection';

const About = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isRTL = i18n.dir() === 'rtl';

  const fontFamily = isRTL ? "'Tajawal', Arial, sans-serif" : "Arial, Helvetica, sans-serif";
  const points = t('Des', { returnObjects: true });

  return (
    <Box sx={{ backgroundColor: '#fff', fontFamily, color: '#333', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <HeroSection />

      {/* About Section - Hero Title */}
      <Box
        sx={{
          width: '100%',
          padding: '60px 0',
          backgroundColor: '#f5f7fa',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography
          variant={isMobile ? 'h4' : 'h2'}
          sx={{
            fontFamily,
            fontWeight: 700,
            color: '#0C2182',
            letterSpacing: 2,
            textTransform: 'uppercase',
            lineHeight: 1.3,
          }}
        >
          {t('aboutUsTitle')}
        </Typography>
     
      </Box>

      {/* Behind the Success Section */}
      <Container maxWidth="xl" sx={{ my: 6 }}>
        <Grid
          container
          spacing={6}
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Text List */}
          <Grid item xs={12} md={6}>
            <List sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
              {points.map((text, index) => (
                <ListItem
                  key={index}
                  sx={{
                    py: 2,
                    px: 0,
                    textAlign: isRTL ? 'right' : 'left',
                    fontFamily,
                    borderLeft: isRTL ? 'none' : '4px solid #0C2182',
                    borderRight: isRTL ? '4px solid #0C2182' : 'none',
                    pl: isRTL ? 2 : 3,
                    pr: isRTL ? 3 : 2,
                    mb: 2,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 1,
                    boxShadow: '0 2px 6px rgba(12, 33, 130, 0.1)',
                    '&:last-child': {
                      mb: 0,
                    },
                  }}
                >
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: {
                        fontFamily,
                        color: '#333',
                        textAlign: 'justify',
                        wordBreak: 'break-word',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Button */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => window.open('/catalog.pdf')}
                sx={{
                  fontWeight: 600,
                  fontFamily,
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 4px 10px rgba(12, 33, 130, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#0a1a63',
                    boxShadow: '0 6px 15px rgba(10, 26, 99, 0.6)',
                  },
                }}
              >
                {t('catalog')}
              </Button>
            </Box>
          </Grid>

          {/* Chairman and Vice Chairman Section */}
          <Grid
            item
            xs={12}
            md={6}
            container
            spacing={4}
            justifyContent="center"
            sx={{ mt: isMobile ? 4 : 0 }}
          >
            {/* Chairman */}
            <Grid item xs={12} sm={6} sx={{ textAlign: 'center' }}>
              <Avatar
                src={Chairman}
                alt="Chairman"
                sx={{
                  width: isMobile ? 120 : 180,
                  height: isMobile ? 120 : 180,
                  mx: 'auto',
                  borderRadius: '50%',
                  border: '4px solid #0C2182',
                  boxShadow: '0 4px 12px rgba(12, 33, 130, 0.3)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: '#1e1e1e', mt: 2, fontFamily }}
              >
                {t('CN')}
              </Typography>
              <Typography
                variant="subtitle2"
                color="#0C2182"
                sx={{ fontFamily, mt: 1 }}
              >
                {t('chairmanTitle')}
              </Typography>
            </Grid>

            {/* Vice Chairman */}
            <Grid item xs={12} sm={6} sx={{ textAlign: 'center' }}>
              <Avatar
                src={ViseChairman}
                alt="Vice Chairman"
                sx={{
                  width: isMobile ? 120 : 180,
                  height: isMobile ? 120 : 180,
                  mx: 'auto',
                  borderRadius: '50%',
                  border: '4px solid #0C2182',
                  boxShadow: '0 4px 12px rgba(12, 33, 130, 0.3)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: '#1e1e1e', mt: 2, fontFamily }}
              >
                {t('VCN')}
              </Typography>
              <Typography
                variant="subtitle2"
                color="#0C2182"
                sx={{ fontFamily, mt: 1 }}
              >
                {t('viceChairmanTitle')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Other Sections */}
      <Box sx={{ pt: 6, bgcolor: '#f5f7fa' }}>
        <MissionVisionSection />
      </Box>
      <Box sx={{ py: 6 }}>
        <OurValues />
      </Box>
      <Box sx={{ py: 1, bgcolor: '#f5f7fa' }}>
        <ImageSlider />
      </Box>
      <Box sx={{ py: 6 }}>
        <PartnersSection />
      </Box>
    </Box>
  );
};

export default About;
